import { api } from './client'
import { demoSite, isDemoMode } from '../utils/demo-mode'
import type {
  AlertSchedule,
  AvailabilityMap,
  AvailabilityState,
  ConversationDetail,
  ConversationListItem,
  ConversationScore,
  Operator,
  Playbook,
  Proposal,
  ProposalStatus,
  ScoresSummary,
  Site,
  SiteId,
  Message,
} from '../types'

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<void> {
  await api('/operator/auth/request', { body: { email }, auth: false })
}

export interface VerifyResponse {
  sessionToken: string
  expiresAt: number
  operator: Operator
  sites: SiteId[]
}

export function verifyMagicCode(
  email: string,
  code: string,
  deviceLabel?: string
): Promise<VerifyResponse> {
  return api<VerifyResponse>('/operator/auth/verify-code', {
    body: { email, code, deviceLabel },
    auth: false,
  })
}

/** Public read-only demo sign-in (Spoke & Sprocket only). */
export function startDemoSession(): Promise<VerifyResponse> {
  return api<VerifyResponse>('/operator/auth/demo', { method: 'POST', auth: false })
}

/** Admin: rebuild the demo tenant's seeded week and wipe live demo chats. */
export const resetDemoTenant = () =>
  api<{ ok: true; conversations: number }>('/operator/demo/reset', { method: 'POST' })

// ── Operator / sites ─────────────────────────────────────────────────────────

export interface MeResponse {
  operator: Operator
  sites: SiteId[]
}
// Demo mode filters every site-bearing response down to the demo tenant so
// real client names never appear while screen recording (see utils/demo-mode).
/** Self-service escalation-alert number (E.164 or UK 07…; '' clears). */
export const setMyPhone = (phone: string) =>
  api<{ phone: string | null }>('/operator/me/phone', { body: { phone } })

/** Self-service display name ('' clears — falls back to email on labels). */
export const setMyName = (name: string) =>
  api<{ name: string | null }>('/operator/me/name', { body: { name } })

/** Self-service alert window (quiet hours). null clears — always alert. */
export const setMySchedule = (schedule: AlertSchedule | null) =>
  api<{ schedule: AlertSchedule | null }>('/operator/me/schedule', { body: { schedule } })

export const getMe = () =>
  api<MeResponse>('/operator/me').then((me) =>
    isDemoMode() ? { ...me, sites: me.sites.filter((s) => s === demoSite()) } : me
  )

// ── Conversations ────────────────────────────────────────────────────────────

export interface InboxCounts {
  active: number
  closed: number
  all: number
  needsReply: number
}

/** Honest 7-day stats — only numbers the data actually supports. */
export interface InboxStats {
  conversations: number
  resolvedPct: number | null
  leads: number
  // Conversations started outside the site's business hours. null = no
  // site has a schedule configured, so the metric isn't measurable.
  outOfHours: number | null
}

export interface ConversationListResponse {
  conversations: ConversationListItem[]
  counts?: InboxCounts
  stats?: InboxStats
}

export function listConversations(params?: {
  status?: string
  limit?: number
  q?: string
  signal?: AbortSignal
}): Promise<ConversationListResponse> {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.q) q.set('q', params.q)
  // Demo mode: scope the whole response — list, tab counts AND the KPI
  // strip — to the demo tenant server-side, so no real-tenant aggregate
  // ever renders while recording.
  if (isDemoMode()) q.set('site', demoSite())
  const qs = q.toString()
  return api<ConversationListResponse>(
    `/operator/conversations${qs ? `?${qs}` : ''}`,
    { signal: params?.signal }
  ).then((res) =>
    isDemoMode()
      ? { ...res, conversations: res.conversations.filter((c) => c.site === demoSite()) }
      : res
  )
}

export function getConversation(
  id: string,
  signal?: AbortSignal
): Promise<ConversationDetail> {
  return api<ConversationDetail>(`/operator/conversations/${id}`, { signal })
}

// ── Reply ────────────────────────────────────────────────────────────────────

export interface ReplyResponse {
  ok: true
  delivery: 'live' | 'late_email'
  message?: Message
  sessionStatus?: string
}

export function sendReply(
  sessionId: string,
  message: string
): Promise<ReplyResponse> {
  return api<ReplyResponse>('/operator/reply', {
    body: { sessionId, message },
  })
}

export function sendTypingPing(
  sessionId: string,
  typing: boolean
): Promise<{ ok: true }> {
  return api<{ ok: true }>('/operator/typing', {
    body: { sessionId, typing },
  })
}

// ── Email transcript ─────────────────────────────────────────────────────────

export function emailTranscript(
  conversationId: string,
  to: string
): Promise<{ ok: true; emailedAt: number }> {
  return api<{ ok: true; emailedAt: number }>(
    `/operator/conversations/${conversationId}/email-transcript`,
    { body: { to } }
  )
}

// ── Availability ─────────────────────────────────────────────────────────────

export function getAvailabilityMap(): Promise<{ availability: AvailabilityMap }> {
  return api<{ availability: AvailabilityMap }>('/operator/availability')
}

export function setSiteAvailability(
  site: SiteId,
  available: boolean
): Promise<{ site: SiteId; availability: AvailabilityState }> {
  return api<{ site: SiteId; availability: AvailabilityState }>(
    '/operator/availability',
    { body: { site, available } }
  )
}

// ── Push ─────────────────────────────────────────────────────────────────────

// ── Playbook ─────────────────────────────────────────────────────────────────

export interface PlaybookResponse {
  site: SiteId
  playbook: Playbook
}

export function getPlaybook(site: SiteId): Promise<PlaybookResponse> {
  return api<PlaybookResponse>(
    `/operator/playbook?site=${encodeURIComponent(site)}`
  )
}

export function savePlaybook(
  site: SiteId,
  playbook: Playbook
): Promise<PlaybookResponse> {
  return api<PlaybookResponse>('/operator/playbook', {
    body: { site, playbook },
  })
}

// ── Knowledge ────────────────────────────────────────────────────────────────

export interface KnowledgeResponse {
  site: SiteId
  knowledgeText: string
  source: 'sites_table' | 'legacy_kv' | 'empty'
}

export const getKnowledge = (site: SiteId) =>
  api<KnowledgeResponse>(
    `/operator/knowledge?site=${encodeURIComponent(site)}`
  )

export const saveKnowledge = (site: SiteId, knowledgeText: string) =>
  api<KnowledgeResponse>('/operator/knowledge', {
    body: { site, knowledgeText },
  })

// ── Sites ───────────────────────────────────────────────────────────────────

export interface SitesListResponse {
  sites: Site[]
}

export const listSites = () =>
  api<SitesListResponse>('/operator/sites', { auth: true }).then((res) =>
    isDemoMode() ? { ...res, sites: res.sites.filter((s) => s.id === demoSite()) } : res
  )

export interface CreateSiteInput {
  id: string
  name: string
  phone?: string | null
  email?: string | null
  primaryColor?: string | null
  whatYouCanAnswer?: string[]
  whatToEscalate?: string[]
  embedOrigins?: string[]
}

export const createSite = (input: CreateSiteInput) =>
  api<{ site: Site }>('/operator/sites', { body: input })

export interface UpdateSiteInput {
  name?: string
  phone?: string | null
  email?: string | null
  primaryColor?: string | null
  whatYouCanAnswer?: string[]
  whatToEscalate?: string[]
  embedOrigins?: string[]
  status?: 'active' | 'paused' | 'archived'
  teaserMessage?: string | null
  starterPrompts?: string[]
  alertChannels?: { whatsapp: boolean; sms: boolean; call: boolean }
}

export const updateSite = (siteId: SiteId, patch: UpdateSiteInput) =>
  api<{ site: Site }>(`/operator/sites/${encodeURIComponent(siteId)}`, {
    method: 'PATCH',
    body: patch,
  })

// ── Quality (C0 proposals + C1 reflection scores) ────────────────────────────

export function getProposals(
  site?: SiteId,
  status?: ProposalStatus
): Promise<{ proposals: Proposal[] }> {
  const params = new URLSearchParams()
  if (site) params.set('site', site)
  if (status) params.set('status', status)
  const q = params.toString()
  return api<{ proposals: Proposal[] }>(`/operator/proposals${q ? `?${q}` : ''}`)
}

export function setProposalStatus(
  id: string,
  status: ProposalStatus
): Promise<{ proposal: Proposal }> {
  return api<{ proposal: Proposal }>(
    `/operator/proposals/${encodeURIComponent(id)}/status`,
    { body: { status } }
  )
}

export interface ProposalApply {
  id: string
  proposalId: string
  site: string
  targetKind: 'playbook' | 'knowledge'
  targetField: string | null
  mode: 'append' | 'replace'
  previousValue: string
  newValue: string
  appliedBy: string
  appliedAt: number
  revertedAt: number | null
  revertedBy: string | null
}

/** C2 write path — apply operator-approved text to a playbook field or the knowledge doc. */
export function applyProposal(
  id: string,
  input: { targetField: string; mode: 'append' | 'replace'; text: string }
): Promise<{ proposal: Proposal; apply: ProposalApply }> {
  return api<{ proposal: Proposal; apply: ProposalApply }>(
    `/operator/proposals/${encodeURIComponent(id)}/apply`,
    { body: input }
  )
}

// ── Tune (C2 conversational improvement copilot) ─────────────────────────────

export interface TuneDraft {
  proposalId: string | null
  targetField: string
  mode: 'append' | 'replace'
  text: string
  rationale: string
}

export function tuneChat(
  site: SiteId,
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  conversationId?: string
): Promise<{ reply: string; drafts: TuneDraft[] }> {
  return api<{ reply: string; drafts: TuneDraft[] }>('/operator/tune', {
    body: conversationId ? { site, messages, conversationId } : { site, messages },
  })
}

/** Apply an ad-hoc Tune draft (no existing proposal) — audited via a 'tune' proposal row. */
export function tuneApply(input: {
  site: SiteId
  targetField: string
  mode: 'append' | 'replace'
  text: string
  summary: string
}): Promise<{ proposal: Proposal; apply: ProposalApply }> {
  return api<{ proposal: Proposal; apply: ProposalApply }>('/operator/tune/apply', {
    body: input,
  })
}

/** One-click restore of the latest apply for this proposal. */
export function revertProposal(
  id: string
): Promise<{ proposal: Proposal; reverted: boolean; drifted: boolean }> {
  return api<{ proposal: Proposal; reverted: boolean; drifted: boolean }>(
    `/operator/proposals/${encodeURIComponent(id)}/revert`,
    { body: {} }
  )
}

export function getScores(
  site?: SiteId
): Promise<{ summary: ScoresSummary; recent: ConversationScore[] }> {
  const q = site ? `?site=${encodeURIComponent(site)}` : ''
  return api<{ summary: ScoresSummary; recent: ConversationScore[] }>(`/operator/scores${q}`)
}

export function getConversationQuality(
  id: string
): Promise<{ score: ConversationScore | null; proposals: Proposal[] }> {
  return api<{ score: ConversationScore | null; proposals: Proposal[] }>(
    `/operator/conversations/${encodeURIComponent(id)}/quality`
  )
}

// ── Push ─────────────────────────────────────────────────────────────────────

export function subscribePush(subscription: PushSubscriptionJSON): Promise<{
  ok: true
  id: string
}> {
  return api<{ ok: true; id: string }>('/operator/push/subscribe', {
    body: {
      subscription: {
        endpoint: subscription.endpoint,
        keys: subscription.keys,
      },
      deviceLabel: navigator.userAgent.slice(0, 60),
    },
  })
}
